import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import SiteNavigation from '../components/SiteNavigation';

const TermsAndConditions: React.FC = () => {
  const [isRadOncExpanded, setIsRadOncExpanded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 120; // Account for fixed header
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="site-canvas legal-page">
      <SiteNavigation currentPage="terms" />

      {/* Header */}
      <header className="border-b border-slate-200/80 pt-16">
        <div className="site-container py-10 sm:py-12">
          <p className="section-kicker">Legal</p>
          <h1 className="section-title">
            Terms and Conditions
          </h1>
          <p className="section-copy mt-3">
            1MEDICINE — Terms and Conditions
          </p>
        </div>
      </header>

      <main className="site-container py-10 sm:py-14">
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[15rem_minmax(0,1fr)]">
          {/* Table of Contents */}
          <aside className="surface-card p-5 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7.5rem)] lg:overflow-y-auto" aria-labelledby="terms-contents-heading">
              <h2 id="terms-contents-heading" className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-900">Table of Contents</h2>
              <nav className="space-y-1" aria-label="Terms and conditions sections">
                <button
                  onClick={() => scrollToSection('introduction')}
                  className="block w-full rounded-md px-2 py-1.5 text-left text-sm text-slate-600 transition-colors hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                >
                  Introduction
                </button>
                <button
                  onClick={() => scrollToSection('acceptance')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm cursor-pointer"
                >
                  1. Acceptance of Terms
                </button>
                <button
                  onClick={() => scrollToSection('user-accounts')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  2. User Accounts
                </button>
                <button
                  onClick={() => scrollToSection('payments')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  3. Payments & Subscriptions
                </button>
                <button
                  onClick={() => scrollToSection('intellectual-property')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  4. Intellectual Property
                </button>
                <button
                  onClick={() => scrollToSection('user-content')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  5. User-Generated Content
                </button>
                <button
                  onClick={() => scrollToSection('medical-disclaimer')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  6. Medical Disclaimer
                </button>
                <button
                  onClick={() => scrollToSection('limitation-liability')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  7. Limitation of Liability
                </button>
                <button
                  onClick={() => scrollToSection('indemnification')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  8. Indemnification
                </button>
                <button
                  onClick={() => scrollToSection('data-privacy')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  9. Data and Privacy
                </button>
                <button
                  onClick={() => scrollToSection('termination')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  10. Termination
                </button>
                <button
                  onClick={() => scrollToSection('governing-law')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  11. Governing Law
                </button>
                <button
                  onClick={() => scrollToSection('no-warranty')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  12. No Warranty
                </button>
                <button
                  onClick={() => scrollToSection('entire-agreement')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  13. Entire Agreement
                </button>
                <button
                  onClick={() => scrollToSection('modules')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  14. Modules and Third-Party Content
                </button>
                <button
                  onClick={() => scrollToSection('rad-onc-brain')}
                  className="block w-full border-l-2 border-emerald-500 pl-3 py-2 text-left text-sm font-semibold text-emerald-700 transition-colors hover:text-emerald-800"
                >
                  15. RadOnc Brain Module
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="block w-full text-left deepseek-text-muted hover:text-white transition-colors py-2 text-sm"
                >
                  16. Contact Information
                </button>
              </nav>
          </aside>

          {/* Main Content */}
          <article className="surface-card min-w-0 p-6 sm:p-8 lg:p-10">
            <div className="prose max-w-none">
              {/* Introduction */}
              <section id="introduction" className="mb-12">
                <div className="deepseek-card rounded-lg p-6 mb-6">
                  <h2 className="text-2xl font-bold text-white mb-4">1MEDICINE — Terms and Conditions</h2>
                  <div className="space-y-2 deepseek-text">
                    <p><strong>Effective Date:</strong> 10-10-2025</p>
                    <p><strong>Operated by:</strong> Red Canvas LLC ("Red Canvas," "we," "our," or "us")</p>
                    <p><strong>Application:</strong> 1Medicine ("App") and all associated modules, extensions, and integrated content systems.</p>
                  </div>
                </div>
              </section>

              {/* Section 1: Acceptance of Terms */}
              <section id="acceptance" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Acceptance of Terms</h2>
                <div className="space-y-4 deepseek-text">
                  <p>By accessing, downloading, or using the App, you agree to be bound by these Terms and Conditions ("Terms"), the Privacy Policy, and any module-specific terms incorporated herein by reference.</p>
                  <p>If you do not agree, you must immediately discontinue use of the App.</p>
                  <p>Red Canvas reserves the right to modify these Terms at any time without notice. Continued use after any update constitutes acceptance of the revised Terms.</p>
                </div>
              </section>

              {/* Section 2: User Accounts */}
              <section id="user-accounts" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">User Accounts</h2>
                <div className="space-y-4 deepseek-text">
                  <p>To access most App features, you must create an account. You agree to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Provide accurate, current, and complete registration details;</li>
                    <li>Maintain confidentiality of your login credentials; and</li>
                    <li>Accept responsibility for all activities under your account.</li>
                  </ul>
                  <p>We may suspend or terminate your account, with or without notice, if we believe your use violates these Terms, applicable laws, or threatens the App's integrity or other users.</p>
                </div>
              </section>

              {/* Section 3: Payments, Subscriptions, and Purchases */}
              <section id="payments" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Payments, Subscriptions, and Purchases</h2>
                <div className="space-y-4 deepseek-text">
                  <p>All payments are <strong>non-refundable</strong> except where required by law.</p>
                  <p>You authorize Red Canvas LLC to automatically bill your selected payment method for recurring subscriptions until canceled.</p>
                  <p>We reserve the right to modify pricing or service tiers at any time with reasonable notice. Continued use after such notice constitutes agreement to the updated pricing.</p>
                  <p>Failure to pay may result in immediate suspension or termination of access.</p>
                </div>
              </section>

              {/* Section 4: Intellectual Property */}
              <section id="intellectual-property" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Intellectual Property</h2>
                <div className="space-y-4 deepseek-text">
                  <p>All content within the App—including source code, software, databases, designs, text, images, and educational material—is the property of Red Canvas LLC or its licensors.</p>
                  <p>You may <strong>not</strong>:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Copy, distribute, or modify App materials without express permission;</li>
                    <li>Use App content to develop, train, or support competing products or AI models;</li>
                    <li>Reverse engineer or attempt to extract source code;</li>
                    <li>Circumvent security, licensing, or access control systems.</li>
                  </ul>
                  <p>All rights not expressly granted remain reserved to Red Canvas LLC.</p>
                </div>
              </section>

              {/* Section 5: User-Generated Content */}
              <section id="user-content" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">User-Generated Content</h2>
                <div className="space-y-4 deepseek-text">
                  <p>By posting, uploading, or contributing any text, media, or educational material ("User Content"), you grant Red Canvas LLC an <strong>irrevocable, worldwide, royalty-free, perpetual, transferable license</strong> to use, display, reproduce, adapt, and distribute such content for any lawful purpose, including marketing or product development.</p>
                  <p>You represent that:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>You own or control all necessary rights to your User Content;</li>
                    <li>Your submissions comply with applicable law;</li>
                    <li>Your content contains no protected health information (PHI) or confidential data.</li>
                  </ul>
                  <p>Red Canvas LLC may remove or modify User Content at its sole discretion without notice.</p>
                </div>
              </section>

              {/* Section 6: Medical Disclaimer */}
              <section id="medical-disclaimer" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Medical Disclaimer</h2>
                <div className="space-y-4 deepseek-text">
                  <p>All materials, features, and modules within the App are provided <strong>for educational purposes only</strong>.</p>
                  <p>They do <strong>not</strong> constitute medical advice or establish a doctor–patient relationship.</p>
                  <p>You agree that:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>App content may be incomplete, inaccurate, or outdated;</li>
                    <li>All clinical or treatment decisions are your own responsibility; and</li>
                    <li>Red Canvas LLC bears no liability for any outcomes based on App content.</li>
                  </ul>
                </div>
              </section>

              {/* Section 7: Limitation of Liability */}
              <section id="limitation-liability" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Limitation of Liability</h2>
                <div className="space-y-4 deepseek-text">
                  <p>To the fullest extent permitted by law, Red Canvas LLC and its affiliates, officers, employees, and agents shall <strong>not be liable</strong> for any direct, indirect, incidental, consequential, or punitive damages arising from or related to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Your use or inability to use the App or its modules;</li>
                    <li>Errors, inaccuracies, or omissions in content; or</li>
                    <li>Loss of data, revenue, goodwill, or patient outcomes.</li>
                  </ul>
                  <p>Your sole remedy is to discontinue use of the App.</p>
                  <p>You waive any right to participate in class or collective proceedings.</p>
                </div>
              </section>

              {/* Section 8: Indemnification */}
              <section id="indemnification" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Indemnification</h2>
                <div className="space-y-4 deepseek-text">
                  <p>You agree to defend, indemnify, and hold harmless Red Canvas LLC, its affiliates, officers, and employees from and against any claims, damages, liabilities, or expenses (including attorneys' fees) arising out of:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Your use or misuse of the App;</li>
                    <li>Your breach of these Terms;</li>
                    <li>Your User Content; or</li>
                    <li>Your violation of law or third-party rights.</li>
                  </ul>
                </div>
              </section>

              {/* Section 9: Data and Privacy */}
              <section id="data-privacy" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Data and Privacy</h2>
                <div className="space-y-4 deepseek-text">
                  <p>We collect limited personal and de-identified information to improve functionality and security.</p>
                  <p>We do <strong>not</strong> store patient health data.</p>
                  <p>You understand that:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Electronic communications are inherently insecure; and</li>
                    <li>You assume all risk related to data transmission.</li>
                  </ul>
                  <p>Use of the App is also governed by the <strong>1Medicine Privacy Policy</strong>, which may be updated from time to time.</p>
                </div>
              </section>

              {/* Section 10: Termination */}
              <section id="termination" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Termination</h2>
                <div className="space-y-4 deepseek-text">
                  <p>Red Canvas may suspend or terminate your account, access, or license at any time, with or without notice, for any reason.</p>
                  <p>Upon termination, all granted rights immediately cease.</p>
                  <p>Sections on intellectual property, limitation of liability, indemnification, and dispute resolution shall survive termination.</p>
                </div>
              </section>

              {/* Section 11: Governing Law and Dispute Resolution */}
              <section id="governing-law" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Governing Law and Dispute Resolution</h2>
                <div className="space-y-4 deepseek-text">
                  <p>These Terms are governed by the laws of the <strong>State of Florida</strong> without regard to conflict of law principles.</p>
                  <p>Any disputes shall be resolved exclusively in the state or federal courts of <strong>Pinellas County, Florida</strong>, unless Red Canvas LLC elects binding arbitration.</p>
                  <p>Arbitration shall take place in <strong>St. Petersburg, Florida</strong>, under the AAA Commercial Arbitration Rules.</p>
                  <p>You waive any right to a jury trial or class action.</p>
                </div>
              </section>

              {/* Section 12: No Warranty */}
              <section id="no-warranty" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">No Warranty</h2>
                <div className="space-y-4 deepseek-text">
                  <p>The App and all related services are provided "as is" and "as available."</p>
                  <p>Red Canvas LLC disclaims all express or implied warranties, including merchantability, fitness for a particular purpose, accuracy, or non-infringement.</p>
                  <p>We make no guarantee of uninterrupted or error-free operation.</p>
                </div>
              </section>

              {/* Section 13: Entire Agreement */}
              <section id="entire-agreement" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Entire Agreement</h2>
                <div className="space-y-4 deepseek-text">
                  <p>These Terms, together with any incorporated policies or module-specific terms, constitute the entire agreement between you and Red Canvas LLC.</p>
                  <p>If any provision is found unenforceable, the remaining terms remain in full force and effect.</p>
                </div>
              </section>

              {/* Section 14: Modules and Third-Party Content */}
              <section id="modules" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Modules and Third-Party Content</h2>
                
                <div className="space-y-6">
                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">A. Overview</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>1Medicine includes independent or co-developed educational and functional components ("Modules"), such as <em>Rad Onc Brain</em>, <em>Oral Boards</em>, or other specialized resources.</p>
                      <p>Each Module may be operated by Red Canvas LLC or a third-party partner.</p>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">B. Ownership</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>Red Canvas LLC owns and controls the App platform, codebase, and interface.</p>
                      <p>Module partners retain ownership of their original module content but grant Red Canvas a license to host, display, and distribute their materials within the App.</p>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">C. Independent Content</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>Modules are provided for educational use only. Red Canvas LLC is not responsible for module accuracy or content created by third-party partners.</p>
                      <p>Use of any module is entirely at your own risk.</p>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">D. Data</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>Red Canvas may collect de-identified usage analytics across modules.</p>
                      <p>Modules may not collect or store patient-identifiable data.</p>
                      <p>Third-party module operators have no access to personal user information except as necessary for their content's operation.</p>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">E. Restrictions</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>Users may not copy, reproduce, resell, or export module content; use it to train AI systems; or integrate it into competing services.</p>
                      <p>Red Canvas may modify or remove modules at its sole discretion without refund or notice.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 15: Module-Specific Terms — Rad Onc Brain */}
              <section id="rad-onc-brain" className="mb-12">
                <div className="deepseek-card rounded-lg p-8 mb-8 border-2 border-blue-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-4xl font-bold text-white mb-4 deepseek-text-gradient">RadOnc Brain Module Terms</h2>
                      <p className="text-lg deepseek-text-muted">
                        Specialized terms and conditions for the RadOnc Brain educational module
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsRadOncExpanded((expanded) => !expanded)}
                      className="ml-4 rounded-full border border-slate-200 bg-white p-3 text-slate-700 transition-colors hover:border-emerald-300 hover:text-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                      aria-expanded={isRadOncExpanded}
                      aria-controls="rad-onc-module-terms"
                      aria-label={isRadOncExpanded ? 'Collapse RadOnc Brain module terms' : 'Expand RadOnc Brain module terms'}
                    >
                      <svg 
                        className={`h-6 w-6 transition-transform duration-200 ${isRadOncExpanded ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Collapsible Content */}
                <div
                  id="rad-onc-module-terms"
                  hidden={!isRadOncExpanded}
                  className={`transition-opacity duration-300 ${isRadOncExpanded ? 'opacity-100' : 'opacity-0'}`}
                >
                  <div className="space-y-4 deepseek-text mb-6">
                    <p>The <strong>Rad Onc Brain</strong> module ("Module") is an educational resource provided within 1Medicine and hosted by Red Canvas LLC on behalf of <strong>Rayos Contra Cancer ("RCC")</strong>, a nonprofit organization.</p>
                    <p>By accessing this Module, you agree to these Module Terms in addition to the 1Medicine Terms above.</p>
                  </div>

                  <div className="space-y-6">
                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">1. Purpose of the Module</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>The Module provides educational information and clinical reference materials in radiation oncology.</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>The Module is for <strong>educational use only</strong> and <strong>not</strong> for medical decision-making.</li>
                        <li>It is not a substitute for independent professional judgment.</li>
                        <li>All patient-care decisions are your sole responsibility.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">2. Eligibility</h3>
                    <div className="space-y-2 deepseek-text">
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>The Module is intended for licensed healthcare professionals and supervised trainees.</li>
                        <li>By using the Module, you confirm you meet these qualifications.</li>
                        <li>Non-medical users must not rely on this Module for clinical or treatment decisions.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">3. Intellectual Property</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>All original content within the Rad Onc Brain Module—including text, graphics, figures, and educational materials—is owned by RCC or its licensors and protected by copyright law.</p>
                      <p>You may:</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>View and use content for personal, non-commercial educational purposes only.</li>
                      </ul>
                      <p>You may <strong>not</strong>:</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Copy, distribute, or modify the Module content;</li>
                        <li>Use it to train or develop competing products, software, or AI models;</li>
                        <li>Reverse engineer or scrape any part of the Module;</li>
                        <li>Reuse or republish figures, data, or derived works without RCC's prior written consent.</li>
                      </ul>
                      <p>All rights not expressly granted remain with RCC.</p>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">4. Third-Party Materials</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>Some materials may be sourced from textbooks, lectures, or other works owned by third parties.</p>
                      <p>RCC does not claim ownership of such materials, which remain the property of their respective copyright holders.</p>
                      <p>Such materials are included for educational purposes only and may not be reused except under applicable fair-use laws.</p>
                      <p>Rights holders may contact RCC regarding concerns at <strong>contact@1medicine.app</strong>.</p>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">5. Acceptable Use</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>You agree not to:</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Use the Module for any unlawful, harmful, or unauthorized purpose;</li>
                        <li>Upload or transmit malicious code;</li>
                        <li>Share your login credentials;</li>
                        <li>Attempt to access data or systems without authorization;</li>
                        <li>Use the Module for commercial gain or competition.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">6. No Warranties</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>The Module and all its contents are provided <strong>"as is"</strong> and <strong>"as available."</strong></p>
                      <p>RCC makes no warranties, express or implied, regarding:</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Accuracy or completeness of the content;</li>
                        <li>Fitness for any purpose;</li>
                        <li>Non-infringement or reliability.</li>
                      </ul>
                      <p>Use is at your sole risk.</p>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">7. Limitation of Liability</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>To the fullest extent permitted by law:</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>RCC, its officers, employees, volunteers, and affiliates are <strong>not liable</strong> for any direct, indirect, incidental, or consequential damages arising from use of the Module;</li>
                        <li>This includes, without limitation, damages related to patient outcomes, data loss, or business interruption;</li>
                        <li>RCC is not responsible for unauthorized access or misuse beyond its reasonable control;</li>
                        <li>Your sole remedy is to discontinue use of the Module.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">8. Indemnification</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>You agree to indemnify and hold harmless RCC and its affiliates, volunteers, and employees from any claims or expenses (including attorneys' fees) arising out of:</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Your use or misuse of the Module;</li>
                        <li>Your reliance on its content for clinical decisions;</li>
                        <li>Your violation of these Terms.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">9. Privacy and Data</h3>
                    <div className="space-y-2 deepseek-text">
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>RCC may collect limited de-identified usage data to maintain and improve the Module.</li>
                        <li>No patient-identifiable information is collected or stored.</li>
                        <li>While reasonable safeguards are maintained, absolute security cannot be guaranteed.</li>
                        <li>RCC follows data practices consistent with the 1Medicine Privacy Policy.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">10. Changes and Termination</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>RCC may modify, update, or discontinue the Module at any time.</p>
                      <p>RCC may also terminate your access for violations of these Terms.</p>
                      <p>Upon termination, you must immediately stop using and delete any stored or cached Module content.</p>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">11. Governing Law and Dispute Resolution</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>These Module Terms are governed by the laws of the <strong>State of Washington, USA</strong>.</p>
                      <p>Disputes will be resolved in the <strong>state or federal courts of Seattle, Washington</strong>, unless RCC elects binding arbitration in Seattle under the AAA Rules.</p>
                      <p>If you access the Module from outside the U.S., you are responsible for compliance with local laws.</p>
                    </div>
                  </div>

                  <div className="deepseek-card rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-3">12. Entire Agreement</h3>
                    <div className="space-y-2 deepseek-text">
                      <p>These Rad Onc Brain Terms, together with the 1Medicine Terms and Privacy Policy, constitute the complete agreement between you, RCC, and Red Canvas LLC regarding Module use.</p>
                      <p>If any provision is found invalid, the remainder remains enforceable.</p>
                    </div>
                  </div>
                </div>
                </div>
              </section>

              {/* Section 16: Contact Information */}
              <section id="contact" className="mb-12">
                <h2 className="text-3xl font-bold text-white mb-6">Contact Information</h2>
                <div className="deepseek-card rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-white mb-4">Red Canvas LLC</h3>
                  <div className="space-y-2 deepseek-text">
                    <p>18117 Biscayne Blvd #1136</p>
                    <p>Miami, FL 33160, USA</p>
                    <p>Email: <a href="mailto:contact@1medicine.app" className="text-blue-400 hover:text-blue-300">contact@1medicine.app</a></p>
                  </div>
                </div>
              </section>
            </div>
          </article>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TermsAndConditions;